/**
 * MFA (Multi-Factor Authentication) Constants
 */

/**
 * Flow stages for MFA configuration modal
 */
export const MFA_FLOW_STAGES = {
    METHOD_CHOICE: 'method_choice',
    AUTHENTICATOR_APP: 'authenticator_app',
    VERIFY_CODE: 'verify_code',
    SAVE_RECOVERY_CODES: 'save_recovery_codes',
    NAME_CONFIGURATION: 'name_configuration',
    COMPLETE: 'complete',
} as const;

export type MFAFlowStage = (typeof MFA_FLOW_STAGES)[keyof typeof MFA_FLOW_STAGES];

/**
 * Source of MFA configuration (affects modal title)
 */
export type MFAConfigSource = 'toggle' | 'button';

/**
 * Default TTL (Time To Live) for MFA sessions in days
 */
export const MFA_DEFAULT_TTL_DAYS = 7;

/**
 * Modal titles for each flow stage
 */
export const MFA_TITLES: Record<string, string | Record<string, string>> = {
    method_choice: {
        toggle: 'Turn on two-step verification',
        button: 'Add two-step verification method',
    },
    authenticator_app: {
        qr: 'Scan code in authenticator app',
        key: 'Add using your setup key',
    },
    verify_code: 'Verify Code',
    save_recovery_codes: 'Save your recovery code',
    name_configuration: 'Name your 2FA method',
    complete: '2FA is ON 🎉',
};

/**
 * Modal descriptions for each flow stage
 */
export const MFA_DESCRIPTIONS: Record<string, string | Record<string, string>> = {
    method_choice: {
        toggle: 'Verify your identity after entering your password with a verification code from an authenticator app.',
        button: 'Two-step verification is already enabled. Change how you verify your identity after entering your password.',
    },
    authenticator_app: {
        qr: '1. Scan the QR code to generate a unique code in your authenticator app',
        key: '1. Copy the secret key below and paste it into your authenticator app',
    },
    save_recovery_codes: 'Be sure to keep this safe to avoid getting locked out of your account',
    name_configuration:
        'This helps you recognize it later. Use the name of the app you used, like Google Authenticator or Authy',
    complete:
        "From now on, you'll need to enter a verification code whenever we don't recognize your device. To keep your account secure, you've been logged out of all other devices.",
};

/**
 * MFA method types
 */
export const MFA_METHODS = {
    AUTHENTICATOR_APP: 'Authenticator app',
} as const;

/**
 * MFA status values
 */
export const MFA_STATUS = {
    ACTIVE: 1,
    INACTIVE: 0,
} as const;

/**
 * Regex to extract secret from TOTP URI
 */
export const TOTP_SECRET_REGEX = /\?secret=(.*)&/;

/**
 * Helper to get title based on flow stage and source
 */
export function getMFATitle(stage: MFAFlowStage, source: MFAConfigSource, showKey = false): string {
    const title = MFA_TITLES[stage];
    if (!title) {
        return '';
    }
    if (typeof title === 'string') {
        return title;
    }
    if (stage === 'authenticator_app') {
        return showKey ? (title.key ?? '') : (title.qr ?? '');
    }
    return title[source] ?? '';
}

/**
 * Helper to get description based on flow stage and source
 */
export function getMFADescription(stage: MFAFlowStage, source: MFAConfigSource, showKey = false): string {
    const description = MFA_DESCRIPTIONS[stage];
    if (!description) {
        return '';
    }
    if (typeof description === 'string') {
        return description;
    }
    if (stage === 'authenticator_app') {
        return showKey ? (description.key ?? '') : (description.qr ?? '');
    }
    return description[source] ?? '';
}

/**
 * Extract secret from TOTP URI
 */
export function extractSecretFromTOTPURI(totpURI: string): string {
    const match = totpURI.match(TOTP_SECRET_REGEX);
    return match?.[1] ?? '';
}
